/**
 * /sso/callback: the far end of the Loop SSO hand-off.
 *
 * SignIn's "Continue with Loop" sends the browser to loop-web's
 * /app/cloud-sso, which mints a short-lived assertion server-to-server and
 * redirects back here with the assertion in the URL FRAGMENT (never the
 * query string, so it never reaches a server log or a Referer header). This
 * screen compares the `state` it gets back against the one it stored before
 * leaving, exchanges the assertion for a cloud-api session, and lands on
 * /projects, or on POST_LOGIN_PATH_KEY's stashed path when a pre-auth
 * screen (OauthAuthorize) needs the browser back on itself instead. A
 * mismatch or a refusal renders the same quiet tone SignIn uses for
 * `oauth-denied`: no blame, and a clear statement that nothing changed.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Notice } from '../../components/patterns/Notice';
import { Wordmark } from '../../components/brand/Wordmark';
import { HeroBackdrop } from '../../components/patterns/HeroBackdrop';
import { Skeleton } from '../../components/patterns/Skeleton';
import { apiFetch, ApiError } from '../../lib/http';
import { setToken, useSessionStore } from '../../lib/auth/session';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import { POST_LOGIN_PATH_KEY, SSO_STATE_KEY } from './sso-state';

import '../../styles/screens/auth.css';
import type { Plan } from '../../lib/types';

interface ExchangeResponse {
  account: {
    id: string;
    email: string;
    display_name: string | null;
    mode: 'simple' | 'advanced';
    language?: 'en' | 'th' | null;
    is_admin: boolean;
    plan_id?: Plan;
    plan_expires_at?: string | null;
    created_at: string;
  };
  token: string;
}

export default function SsoCallback() {
  const navigate = useNavigate();
  const setAccount = useSessionStore((s) => s.setAccount);
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const assertion = hash.get('assertion');
    const state = hash.get('state');
    // Clear the fragment immediately: it holds a bearer credential and this
    // page is done reading it either way.
    window.history.replaceState(null, '', window.location.pathname);

    const expectedState = sessionStorage.getItem(SSO_STATE_KEY);
    sessionStorage.removeItem(SSO_STATE_KEY);

    if (!assertion || !state || !expectedState || state !== expectedState) {
      captureEvent('loop_sso_mismatch_or_denied', {
        has_assertion: Boolean(assertion),
        has_state: Boolean(state),
        has_expected: Boolean(expectedState),
      });
      setError(t("You didn't finish signing in with Loop. Nothing has changed on your account."));
      return;
    }

    apiFetch<ExchangeResponse>('/api/v1/auth/loop/exchange', {
      method: 'POST',
      body: { assertion },
      unauthenticated: true,
    })
      .then((res) => {
        captureEvent('loop_sso_success', {
          email: res.account.email,
          isAdmin: res.account.is_admin,
        });
        setToken(res.token);
        setAccount({
          id: res.account.id,
          name: res.account.display_name ?? res.account.email.split('@')[0] ?? res.account.email,
          email: res.account.email,
          plan: res.account.plan_id ?? 'basic',
          planExpiresAt: res.account.plan_expires_at ?? null,
          detailLevel: res.account.mode,
          language: res.account.language ?? null,
          createdAt: res.account.created_at,
          githubHandle: '',
          isAdmin: res.account.is_admin,
          // Real value arrives from getAccount() on shell mount; default off until then.
          tenantStagingEnabled: false,
          teamsEnabled: false,
          svgitEnabled: false,
        });
        const postLoginPath = sessionStorage.getItem(POST_LOGIN_PATH_KEY);
        sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
        navigate(postLoginPath ?? '/projects', { replace: true });
      })
      .catch((err) => {
        const code = err instanceof ApiError ? err.code : 'unknown';
        captureEvent('loop_sso_failed', { error_code: code });
        if (err instanceof ApiError && err.code === 'SSO_NOT_ELIGIBLE') {
          setError(t("SV Cloud isn't open to your account yet."));
        } else {
          setError(t('Something went wrong connecting to Loop. Try again in a moment.'));
        }
      });
  }, [navigate, setAccount]);

  return (
    <div className="auth">
      <HeroBackdrop reach="tight" />
      <div className="auth__lockup">
        <Wordmark size={32} />
      </div>
      <div className="auth__card">
        {error ? (
          <>
            <Notice tone="attention">
              <p>{error}</p>
            </Notice>
            <p className="auth__foot">
              <Link className="link-inline" to="/sign-in">
                {t('Back to sign in')}
              </Link>
            </p>
          </>
        ) : (
          <>
            <h1 className="auth__title">{t('Finishing sign-in')}</h1>
            <Skeleton label={t('Connecting your Loop account')} />
          </>
        )}
      </div>
    </div>
  );
}
